/* eslint-disable */
import type { Money } from "../../../../common/currency.pb";
import type { CurrencyCode } from "../../../../common/currency_codes.pb";
import type { YearMonth, YearMonthDay } from "../../../../common/time.pb";

export enum SavingsCustomerStatus {
  SAVINGS_CUSTOMER_STATUS_UNSPECIFIED = "SAVINGS_CUSTOMER_STATUS_UNSPECIFIED",
  SAVINGS_CUSTOMER_STATUS_INELIGIBLE = "SAVINGS_CUSTOMER_STATUS_INELIGIBLE",
  SAVINGS_CUSTOMER_STATUS_ELIGIBLE = "SAVINGS_CUSTOMER_STATUS_ELIGIBLE",
  SAVINGS_CUSTOMER_STATUS_ADOPTED = "SAVINGS_CUSTOMER_STATUS_ADOPTED",
}

export enum YieldEligibilityStatus {
  YIELD_ELIGIBILITY_STATUS_UNSPECIFIED = "YIELD_ELIGIBILITY_STATUS_UNSPECIFIED",
  YIELD_ELIGIBILITY_STATUS_INELIGIBLE = "YIELD_ELIGIBILITY_STATUS_INELIGIBLE",
  /**
   * YIELD_ELIGIBILITY_STATUS_ELIGIBLE - Note that this status name isn't accurate anymore. We serve this to mean the customer is
   * eligible to access the yield onboarding flow and self onboard to yield. In some cases
   * (eg teens) a customer might be eligible for yield but they cannot onboard on their own. In this
   * case we would serve the YIELD_ELIGIBILITY_STATUS_INELIGIBLE.
   */
  YIELD_ELIGIBILITY_STATUS_ELIGIBLE = "YIELD_ELIGIBILITY_STATUS_ELIGIBLE",
}

export enum InterestYieldStatus {
  INTEREST_YIELD_STATUS_DEFAULT_UNSPECIFIED = "INTEREST_YIELD_STATUS_DEFAULT_UNSPECIFIED",
  INTEREST_YIELD_STATUS_UNONBOARDED = "INTEREST_YIELD_STATUS_UNONBOARDED",
  INTEREST_YIELD_STATUS_ACTIVE = "INTEREST_YIELD_STATUS_ACTIVE",
  /**
   * INTEREST_YIELD_STATUS_DISMISSED - DISMISSED is a deprecated yield status that was never launched to real customers, but it still exists at the DB
   * level in piggybank, so we cannot delete it outright.
   *
   * @deprecated
   */
  INTEREST_YIELD_STATUS_DISMISSED = "INTEREST_YIELD_STATUS_DISMISSED",
  INTEREST_YIELD_STATUS_INACTIVE = "INTEREST_YIELD_STATUS_INACTIVE",
  INTEREST_YIELD_STATUS_MANUALLY_DISABLED = "INTEREST_YIELD_STATUS_MANUALLY_DISABLED",
}

export enum InterestRateTier {
  INTEREST_RATE_TIER_DEFAULT_UNSPECIFIED = "INTEREST_RATE_TIER_DEFAULT_UNSPECIFIED",
  INTEREST_RATE_TIER_STANDARD = "INTEREST_RATE_TIER_STANDARD",
  INTEREST_RATE_TIER_ENHANCED = "INTEREST_RATE_TIER_ENHANCED",
  INTEREST_RATE_TIER_BLOCK_EMPLOYEE_TEST = "INTEREST_RATE_TIER_BLOCK_EMPLOYEE_TEST",
}

export enum BackupWithholdingStatus {
  BACKUP_WITHHOLDING_STATUS_UNSPECIFIED = "BACKUP_WITHHOLDING_STATUS_UNSPECIFIED",
  BACKUP_WITHHOLDING_STATUS_UNKNOWN = "BACKUP_WITHHOLDING_STATUS_UNKNOWN",
  BACKUP_WITHHOLDING_STATUS_NONE = "BACKUP_WITHHOLDING_STATUS_NONE",
  BACKUP_WITHHOLDING_STATUS_SUBJECT_TO = "BACKUP_WITHHOLDING_STATUS_SUBJECT_TO",
}

export enum ResolveBNoticeSubmissionStatus {
  RESOLVE_B_NOTICE_SUBMISSION_STATUS_DEFAULT_UNSPECIFIED = "RESOLVE_B_NOTICE_SUBMISSION_STATUS_DEFAULT_UNSPECIFIED",
  RESOLVE_B_NOTICE_SUBMISSION_STATUS_SUBMISSION_NOT_NEEDED = "RESOLVE_B_NOTICE_SUBMISSION_STATUS_SUBMISSION_NOT_NEEDED",
  RESOLVE_B_NOTICE_SUBMISSION_STATUS_AWAITING_SUBMISSION = "RESOLVE_B_NOTICE_SUBMISSION_STATUS_AWAITING_SUBMISSION",
  RESOLVE_B_NOTICE_SUBMISSION_STATUS_SUBMITTED = "RESOLVE_B_NOTICE_SUBMISSION_STATUS_SUBMITTED",
}

export enum AdditionalEnablementScope {
  ADDITIONAL_ENABLEMENT_SCOPE_UNSPECIFIED = "ADDITIONAL_ENABLEMENT_SCOPE_UNSPECIFIED",
  ADDITIONAL_ENABLEMENT_SCOPE_SELF = "ADDITIONAL_ENABLEMENT_SCOPE_SELF",
  ADDITIONAL_ENABLEMENT_SCOPE_FAMILY = "ADDITIONAL_ENABLEMENT_SCOPE_FAMILY",
}

export interface SavingsCustomerConfig {
  currency_code?: CurrencyCode;
  min_goal_amount?: Money;
  max_goal_amount?: Money;
}

export interface SavingsGoal {
  token?: string;
  /**
   * deprecated, prefer `title`
   *
   * @deprecated
   */
  emoji_text?: string;
  /** this is the goal target amount */
  goal_amount?: Money;
  goal_state?: SavingsGoalGoalState;
  title?: SavingsGoalEmoji;
  label?: string;
  goal_schema_version?: SavingsGoalGoalSchemaVersion;
  is_active?: boolean;
  /**
   * the current goal balance amount
   * TODO this is a new field and only available in calls to PiggybankService.GetGoals
   */
  goal_balance_amount?: Money;
  /**
   * the balance version. note that the goal definition has a separate version tracking updates to
   * goal edits and state changes.
   * TODO this is a new field and only available in calls to PiggybankService.GetGoals
   */
  goal_balance_version?: number;
  /** epoch millis when this goal row was last updated. */
  updated_at?: number;
  /** epoch millis when this goal balance row was last updated. */
  goal_balance_updated_at?: number;
}

export enum SavingsGoalGoalState {
  GOAL_STATE_DEFAULT_UNSPECIFIED = "GOAL_STATE_DEFAULT_UNSPECIFIED",
  GOAL_STATE_IN_PROGRESS = "GOAL_STATE_IN_PROGRESS",
  GOAL_STATE_COMPLETED = "GOAL_STATE_COMPLETED",
  GOAL_STATE_REPLACED = "GOAL_STATE_REPLACED",
  GOAL_STATE_CANCELLED = "GOAL_STATE_CANCELLED",
}

export enum SavingsGoalGoalSchemaVersion {
  GOAL_SCHEMA_VERSION_DEFAULT_UNSPECIFIED = "GOAL_SCHEMA_VERSION_DEFAULT_UNSPECIFIED",
  GOAL_SCHEMA_VERSION_V1 = "GOAL_SCHEMA_VERSION_V1",
  GOAL_SCHEMA_VERSION_V2 = "GOAL_SCHEMA_VERSION_V2",
}

export interface SavingsGoalEmoji {
  /** Emoji name, uniquely identifying */
  name?: string;
  /** Emoji text, such as 💰 */
  text?: string;
  /** Accessibility text, in english */
  accessibility_text?: string;
  /** True if selectable. Inactive emoji are still viewable. */
  active?: boolean;
}

/** SavingsBalance is piggybank's projection of the banklin Savings StoredValueBalance. */
export interface SavingsBalance {
  /**
   * Deprecated in favor of `stored_value_balance_token`.
   *
   * @deprecated
   */
  token?: string;
  /** Balance total amount */
  amount?: Money;
  /** The token of the savings StoredValueBalance object. */
  stored_value_balance_token?: string;
  customer_token?: string;
  /** epoch millis when this projected balance row was last updated. */
  updated_at?: number;
}

export interface GeneralSavings {
  /** Equals the token of the parent, savings StoredValueBalance. */
  token?: string;
  amount?: Money;
}

export interface SavingsCustomer {
  token?: string;
  auto_save_upsell_dismissed_at?: string;
  /**
   * Deprecated: prefer `customer_status`
   *
   * @deprecated
   */
  eligible_for_savings?: boolean;
  customer_status?: SavingsCustomerStatus;
  /**
   * Set to YIELD_ELIGIBILITY_STATUS_ELIGIBLE when the customer is eligible for the yield
   * onboarding flow. This is to preserve backwards compatibility with the usage of this field.
   * If we need to use this field for other eligibility scenarios we can revisit this proto to
   * capture the more granular yield scenarios.
   */
  yield_eligibility_status?: YieldEligibilityStatus;
}

export interface InterestTiersApyRates {
  standard_interest_apy_rate?: number;
  enhanced_interest_apy_rate?: number;
}

export interface InterestRate {
  apy_rate?: number;
  interest_yield_status?: InterestYieldStatus;
  interest_tiers_apy_rates?: InterestTiersApyRates;
}

export interface InterestAccrualRange {
  /** Inclusive */
  start_date?: YearMonthDay;
  /** Exclusive */
  end_date?: YearMonthDay;
  interest_rate?: number;
  interest_tier?: InterestRateTier;
  /** The amount accrued represented as a string to maintain BigDecimal precision */
  amount?: string;
}

export interface InterestPayout {
  /** Epoch time in milliseconds */
  paid_out_at?: number;
  /** The amount paid out represented as a string in dollars to maintain BigDecimal precision */
  full_precision_dollar_amount?: string;
  paid_out_amount?: Money;
  state?: string;
  idempotence_token?: string;
  payment_token?: string;
  accrual_month?: YearMonth;
  /** Override used for banklin */
  transaction_validation_override?: string;
}

/**
 * Deprecated. Use SavingsSubBalance instead.
 *
 * @deprecated
 */
export interface SavingsTarget {
  /** accommodates Goal and General Savings targets */
  type?: SavingsTargetSavingsTargetType;
  /** non-null token is expected for SAVINGS_TARGET_TYPE_GOAL */
  token?: string;
}

export enum SavingsTargetSavingsTargetType {
  SAVINGS_TARGET_TYPE_UNSPECIFIED = "SAVINGS_TARGET_TYPE_UNSPECIFIED",
  SAVINGS_TARGET_TYPE_GENERAL = "SAVINGS_TARGET_TYPE_GENERAL",
  SAVINGS_TARGET_TYPE_GOAL = "SAVINGS_TARGET_TYPE_GOAL",
}

/**
 * SavingsSubBalance accommodates Goal and General Savings sub-balances.
 * It replaces the deprecated SavingsTarget.
 */
export interface SavingsSubBalance {
  goal_balance?: SavingsGoalBalance | undefined;
  general_balance?: SavingsGeneralBalance | undefined;
}

export interface SavingsGeneralBalance {
  /** Optional. This is used if the savings balance stored_value_token is needed. */
  stored_value_token?: string;
}

export interface SavingsGoalBalance {
  /** Non-null goal token is required for SavingsGoalBalance */
  goal_token?: string;
}

/**
 * Proto representing a transfer in savings domain. This may be a:
 * a moneta transfer (external cash in)
 * balance-mover transfer (internal cash-in / cash-out)
 * interest payment
 * goal transfer (general savings <-> goal)
 *
 * Note that not all top-level fields may apply for each transfer type.
 */
export interface AdminSavingsTransfer {
  transfer_type?: AdminSavingsTransferTransferType;
  /** format of the token will be unique to the transfer type */
  token?: string;
  amount?: Money;
  /**
   * source_token may have the format:
   * B$_svngs_ (SavingsBalanceToken)
   * SVGL_ (GoalToken)
   * B$_ (cash balance)
   * (moneta instrument)
   */
  source_token?: string;
  target_token?: string;
  state?: string;
  idempotence_token?: string;
  /** epoch millis field */
  created_at?: number;
  /** epoch millis field */
  updated_at?: number;
  /**
   * key is the field name. Examples:
   * full_precision_amount
   * accural_month
   * failed_at
   * goal_reason
   */
  metadata?: { [key: string]: MetadataValue };
  /** indicates this is an automatic recurring savings transfer */
  is_recurring_transfer?: boolean;
  /** for failed transfers, this is the failure reason from the underlying transfer origin */
  failure_reason?: string;
}

/** corresponding `AdminSavingsTransfer.token` listed below as code comment */
export enum AdminSavingsTransferTransferType {
  TRANSFER_TYPE_DEFAULT_UNSPECIFIED = "TRANSFER_TYPE_DEFAULT_UNSPECIFIED",
  /** TRANSFER_TYPE_INTEREST_PAYMENT - Token format: `SIP_<string>` */
  TRANSFER_TYPE_INTEREST_PAYMENT = "TRANSFER_TYPE_INTEREST_PAYMENT",
  /** TRANSFER_TYPE_EXTERNAL_TRANSFER - Token format: `MCI_<string>` */
  TRANSFER_TYPE_EXTERNAL_TRANSFER = "TRANSFER_TYPE_EXTERNAL_TRANSFER",
  /** TRANSFER_TYPE_INTERNAL_TRANSFER - Token format: `BMIT_<string>` */
  TRANSFER_TYPE_INTERNAL_TRANSFER = "TRANSFER_TYPE_INTERNAL_TRANSFER",
  /** TRANSFER_TYPE_GOAL_TRANSFER - Token format: `SIGT_<string>` */
  TRANSFER_TYPE_GOAL_TRANSFER = "TRANSFER_TYPE_GOAL_TRANSFER",
}

export interface AdminSavingsTransferMetadataEntry {
  key: string;
  value?: MetadataValue;
}

export interface MetadataValue {
  string_value?: string;
  money_value?: Money;
  integer_value?: number;
  yearmonth_value?: YearMonth;
  epoch_millis_value?: number;
}
